import sanitizeHtml from 'sanitize-html';

const HTML_SEPARATOR_TAG_REGEX = /<\/?(?:p|div|br|li)\b[^>]*>/gi;
const HTML_NBSP_MARK = '&nbsp;';
const HTML_PARAGRAPH_TAG_REGEX = /<p[\s>]/i;
const FORBIDDEN_INLINE_STYLE_PROPS =
	/(?:^|;)\s*(?:font-size|position|float|width|height|display|flex|flex-direction|gap|min-height|min-width|letter-spacing|background|background-image|list-style|list-style-image|border-image|content|cursor|mask|mask-image|filter)\s*:[^;]*/gi;
const CSS_URL_FUNCTION = /url\s*\([^)]*\)/gi;

export interface ISanitizeHtmlOptions {
	allowImages?: boolean;
	keepStyles?: boolean;
	stripLayoutStyles?: boolean;
}

const decodeCommonHtmlEntities = (str: string): string =>
	str
		.replace(/&quot;/g, '"')
		.replace(/&#39;|&apos;/g, "'")
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&nbsp;/g, ' ')
		.replace(/&amp;/g, '&');

/**
 * @param options - Boolean keepStyles (legacy) or a structured options object
 * @returns Normalized sanitizer options
 */
function resolveSanitizeOptions(options: boolean | ISanitizeHtmlOptions): ISanitizeHtmlOptions {
	if (typeof options === 'boolean') {
		return { keepStyles: options, allowImages: true, stripLayoutStyles: false };
	}

	return {
		keepStyles: options.keepStyles !== false,
		allowImages: options.allowImages !== false,
		stripLayoutStyles: Boolean(options.stripLayoutStyles),
	};
}

/**
 * @param html - Sanitized HTML
 * @returns HTML without forbidden layout style properties
 */
function stripLayoutStyles(html: string): string {
	return html.replace(/style="([^"]*)"/gi, (_match, styleValue: string) => {
		const cleaned = styleValue
			.replace(FORBIDDEN_INLINE_STYLE_PROPS, '')
			.replace(CSS_URL_FUNCTION, '')
			.replace(/^;+|;+$/g, '')
			.trim();
		return cleaned ? `style="${cleaned}"` : '';
	});
}

/**
 * Sanitizes HTML for application templates.
 * @param str - Raw HTML
 * @param options - Legacy `keepStyles` boolean, or structured options
 * @returns Sanitized HTML
 */
export const sanitize = (str: string, options: boolean | ISanitizeHtmlOptions = true) => {
	const resolved = resolveSanitizeOptions(options);
	const allowedTags = ['p', 'span', 'li', 'ul', 'ol', 'strong', 'em', 'a', 'br'];
	const allowedAttributes: Record<string, string[]> = {
		p: ['style'],
		span: ['style'],
		li: ['style'],
		ul: ['style'],
		ol: ['style'],
		strong: ['style'],
		em: ['style'],
		a: ['style', 'href', 'target'],
	};

	if (resolved.allowImages) {
		allowedTags.push('img');
		allowedAttributes.img = ['src', 'alt', 'width', 'height', 'style'];
	}

	const allowedStyles = !resolved.keepStyles
		? {
				'*': {
					'text-decoration': [/^underline$/],
				},
		  }
		: undefined;

	const sanitized = sanitizeHtml(str, {
		allowedTags,
		allowedAttributes,
		allowedStyles,
	});

	return resolved.stripLayoutStyles ? stripLayoutStyles(sanitized) : sanitized;
};

export const hasApplicationTemplateHtmlMarkup = (text: string): boolean =>
	text.includes(HTML_NBSP_MARK) || HTML_PARAGRAPH_TAG_REGEX.test(text);

export interface IResolvedConsentBody {
	html: string | null;
	plainText: string | null;
	usedPlainTextFallback: boolean;
}

/**
 * Resolves consent metadata.text for print: sanitized HTML, or a plain-text
 * fallback when the sanitizer removes all visible content.
 * @param text - Question metadata text
 * @returns Render mode for the consent heading
 */
export function resolveApplicationTemplateQuestionBody(text: string | null | undefined): IResolvedConsentBody {
	if (!text) {
		return { html: null, plainText: null, usedPlainTextFallback: false };
	}

	if (!hasApplicationTemplateHtmlMarkup(text)) {
		return { html: null, plainText: text, usedPlainTextFallback: false };
	}

	const sanitized = sanitize(text, { stripLayoutStyles: true, allowImages: false }).trim();
	if (!htmlHasVisibleText(sanitized)) {
		return {
			html: null,
			plainText: sanitizePlainText(text) || toPlainConsentFallback(text),
			usedPlainTextFallback: true,
		};
	}

	return { html: sanitized, plainText: null, usedPlainTextFallback: false };
}

/**
 * @param html - Sanitized markup
 * @returns True when the markup still contains visible text
 */
function htmlHasVisibleText(html: string): boolean {
	return Boolean(toPlainConsentFallback(html));
}

/**
 * @param html - Markup that sanitized to no visible text
 * @returns Visible plain-text fallback
 */
function toPlainConsentFallback(html: string): string {
	return html
		.replace(/<[^>]+>/g, ' ')
		.replace(/&nbsp;/gi, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

export const sanitizePlainText = (str?: string | null): string =>
	decodeCommonHtmlEntities(
		sanitizeHtml((str ?? '').replace(HTML_SEPARATOR_TAG_REGEX, ' '), {
			allowedTags: [],
			allowedAttributes: {},
		})
	)
		.replace(/\s+/g, ' ')
		.trim();
